import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { Fade } from '@progress/kendo-react-animation';
import { SvgIcon } from '@progress/kendo-react-common';
import { TextBox } from '@progress/kendo-react-inputs';
import { useLocalization } from '@progress/kendo-react-intl';
import {
    ListBox, ListBoxToolbar, processListBoxData,
    processListBoxDragAndDrop, ListBoxToolbarClickEvent,
    ListBoxDragEvent, ListBoxItemClickEvent
} from '@progress/kendo-react-listbox';
import { NotificationGroup, Notification } from '@progress/kendo-react-notification';
import { searchIcon } from '@progress/kendo-svg-icons';
import axios from 'axios';

import { useEffect, useState } from 'react';
import { AccessToken } from '../../../config/AccessToken';
import { loadingPanel } from '../../LoadingPanel';
import { useOutletContext } from 'react-router';


/**
 * ConfigDevicesEnable is a React component that manages the enabling and disabling
 * of devices within a list view. It provides functionalities to search, select, drag,
 * and drop devices between active and inactive lists. The component interacts with
 * a server to fetch and update device states, using access tokens for authorization.
 * Notifications are displayed upon successful operations, and localization is supported
 * for different languages. The component maintains state for device lists, search results,
 * selected devices, and loading/error messages.
 */

const MyCustomItem = (props: any) => {
    let { dataItem, selected, ...others } = props;
    return (
        <li {...others}>
            <div>
                <span>{props.dataItem.name} ({props.dataItem.mac})</span>
            </div>
        </li>
    );
};

const SELECTED_FIELD = 'selected';

export const ConfigDevicesEnable = () => {

    const outletContext = useOutletContext<{ drawerExpanded?: boolean } | null>();
    const drawerExpanded = Boolean(outletContext?.drawerExpanded);
        
    const SERVER_URL = process.env.REACT_APP_SERVER_URL;

    const [activeDevicesInfo, setActiveDevicesInfo] = useState<any>([]);
    const [inactiveDevicesInfo, setInactiveDevicesInfo] = useState<any>([]);

    const [searchActiveDevice, setSearchActiveDevice] = useState<any[] | null>(null);
    const [searchInactiveDevice, setSearchInactiveDevice] = useState<any[] | null>(null);

    const [messageEnabledDevice, setMessageEnabledDevice] = useState<boolean>(false);
    const [messageDisabledDevice, setMessageDisabledDevice] = useState<boolean>(false);
    
    const [messageEnabledDevices, setMessageEnabledDevices] = useState<boolean>(false);
    const [messageDisabledDevices, setMessageDisabledDevices] = useState<boolean>(false);


    const [state, setState] = useState<any>({
        activeDevices: searchActiveDevice,
        inactiveDevices: searchInactiveDevice,
        draggedItem: {}
    });

    const [instance, accessTokenRequest] = AccessToken();

    const [error, setError] = useState('');

    const [loading, setLoading] = useState(false);

    const localizationService = useLocalization();

    let languageId = localStorage.getItem('languageId');

    const userType = localStorage.getItem('userType');
    const canTransferDevices = userType !== 'UserAdminOnlyLocator';

    const handleItemClick = (event: any, activeDevices: any, inactiveDevices: any) => {

        setState({
            ...state,
            [activeDevices]: state[activeDevices]?.map((item: any) => {
                if (item.mac === event?.dataItem?.mac) {
                    item[SELECTED_FIELD] = !item[SELECTED_FIELD];
                } else if (!event.nativeEvent.ctrlKey) {
                    item[SELECTED_FIELD] = false;
                }
                return item;                
            }),
            [inactiveDevices]: state[inactiveDevices].map((item: any) => {
                item[SELECTED_FIELD] = false;
                return item;
            })
        });        
    }

    const handleToolBarClick = (e: ListBoxToolbarClickEvent) => {
        let toolName: any = e.toolName

        let result: any = processListBoxData(state.activeDevices, state.inactiveDevices, toolName, SELECTED_FIELD);
        setState({
            ...state,
            activeDevices: result.listBoxOneData,
            inactiveDevices: result.listBoxTwoData
        });
        setSearchActiveDevice(result.listBoxOneData);
        setSearchInactiveDevice(result.listBoxTwoData);
        setActiveDevicesInfo(result.listBoxOneData);
        setInactiveDevicesInfo(result.listBoxTwoData);

        let isEnabled: boolean = toolName == 'transferTo' ? false : toolName == 'transferAllTo' ? false : true

        let deviceMacSelected: any = []

        if (toolName == 'transferTo' || toolName == 'transferAllTo') {
            state?.activeDevices?.forEach((device: any) => {
                if (device.selected) {
                    deviceMacSelected.push(device.mac)
                }
                device[SELECTED_FIELD] = false;
            });
        }
        else {
            state?.inactiveDevices?.forEach((device: any) => {
                if (device.selected) {
                    deviceMacSelected.push(device.mac)
                }
                device[SELECTED_FIELD] = false;
            });
        }
        
        if (deviceMacSelected.length == 1) {
            setEnableDeviceAPI(deviceMacSelected, isEnabled)
        }
        else{
            if(isEnabled){
                setEnableDevicesListAPI(deviceMacSelected, null)
            }else{
                setEnableDevicesListAPI(null, deviceMacSelected)
            }
        }
        //getDeviceList()
    }

    const handleDragStart = (e: ListBoxDragEvent) => {
        setState({
            ...state,
            draggedItem: e.dataItem
        });
    }

    const handleDrop = (e: ListBoxDragEvent) => {
        let result: any = processListBoxDragAndDrop(state.activeDevices, state.inactiveDevices, state.draggedItem, e.dataItem, 'mac');
        setState({
            ...state,
            activeDevices: result.listBoxOneData,
            inactiveDevices: result.listBoxTwoData
        });

        // Keep rendered list sources in sync with drag-and-drop result.
        setSearchActiveDevice(result.listBoxOneData);
        setSearchInactiveDevice(result.listBoxTwoData);
        setActiveDevicesInfo(result.listBoxOneData);
        setInactiveDevicesInfo(result.listBoxTwoData);

        const isEnabled = result.listBoxOneData.find((item:any) => item.mac == state.draggedItem.mac);

        const itemStatus:any = isEnabled ? true : false;
        let deviceMacSelected: any = []

        if (itemStatus) {
            state?.activeDevices?.forEach((device: any) => {
                if (device.selected) {
                    deviceMacSelected.push(device.mac)
                }
            });
        }
        else {
            state?.inactiveDevices?.forEach((device: any) => {
                if (device.selected) {
                    deviceMacSelected.push(device.mac)
                }
            });
        }

        setEnableDeviceAPI(state.draggedItem.mac, itemStatus)
    }

    const getDeviceList = async () => {

        setLoading(true);

        const commonOperations = (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.get(`${SERVER_URL}/device/list`, { headers })
                .then((res: any) => {

                    if(res == '' || res?.data?.length == 0){
                        setLoading(false);
                        return
                    }
                    
                    const data: any = res?.data?.data

                    const activeDevice: any = []
                    const inactiveDevice: any = []
                    data?.forEach((device: any) => {
                        if (device.isEnabled) {
                            device.selected = false;
                            activeDevice.push(device)
                        } else {
                            device.selected = false;
                            inactiveDevice.push(device)
                        }
                    });

                    setActiveDevicesInfo(activeDevice);
                    setInactiveDevicesInfo(inactiveDevice);

                    setState({
                        ...state,
                        activeDevices: activeDevice,
                        inactiveDevices: inactiveDevice,
                        draggedItem: {}
                    });
                    setLoading(false);
                }).catch((data) => {
                    //setError(data?.message);
                    setLoading(false);
                });
        }

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        }
        else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                // Acquire token silent success
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    }

    useEffect(() => {
        getDeviceList()
    }, [])


    const setEnableDeviceAPI = async (deviceMacSelected: any, isEnabled: any) => {

        const commonOperations = (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.put(`${SERVER_URL}/device/${deviceMacSelected}/${isEnabled}`, null, { headers })
                .then(() => {
                    if(isEnabled){
                        setMessageEnabledDevice(true)
                        if (!messageEnabledDevice) {
                            setTimeout(() => {
                                setMessageEnabledDevice(false);
                            }, 3000);
                        }                        
                    }else{
                        setMessageDisabledDevice(true)
                        if (!messageDisabledDevice) {
                            setTimeout(() => {
                                setMessageDisabledDevice(false);
                            }, 3000);
                        }                        
                    }
                    //getDeviceList()
                }).catch((data) => {
                    setError(data?.message);
                });
        }

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        }
        else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                // Acquire token silent success
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    }

    const setEnableDevicesListAPI = async (devicesEnabledMacList: any, devicesDisabledMacList: any) => {

        const commonOperations = (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            const params = {
                'enableMacList': devicesEnabledMacList ?? [],
                'disableMacList': devicesDisabledMacList ?? []
            };
            axios.put(`${SERVER_URL}/device/list/enable`, params, { headers })
                .then(() => {
                    if(devicesEnabledMacList != null){
                        setMessageEnabledDevices(true)
                        if (!messageEnabledDevices) {
                            setTimeout(() => {
                                setMessageEnabledDevices(false);
                            }, 3000);
                        }                        
                    }else{
                        setMessageDisabledDevices(true)
                        if (!messageDisabledDevices) {
                            setTimeout(() => {
                                setMessageDisabledDevices(false);
                            }, 3000);
                        }                        
                    }
                }).catch((data) => {
                    setError(data?.message);
                });
        }

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        }
        else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                // Acquire token silent success
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    }

    const onSearchActiveDevice = (ev: any) => {
        const value = ev.value?.toString().toLowerCase() || '';

        const newData = activeDevicesInfo.filter((device: any) => {
            const name = device.name?.toString().toLowerCase() || '';
            const mac = device.mac?.toString().toLowerCase() || '';
            return name.includes(value) || mac.includes(value);
        });

        setSearchActiveDevice(newData);
    };
    
    const onSearchInactiveDevice = (ev: any) => {
        const value = ev.value?.toString().toLowerCase() || '';

        const newData = inactiveDevicesInfo.filter((device: any) => {
            const name = device.name?.toString().toLowerCase() || '';
            const mac = device.mac?.toString().toLowerCase() || '';
            return name.includes(value) || mac.includes(value);
        });

        setSearchInactiveDevice(newData);
    };    

    const devicesEnableCss = `
        .devices-enable-root {
            width: min(1320px, 96%);
            height: 100%;
            margin: 0 auto;
            display: flex;
            flex-direction: column;
            gap: 10px;
            position: relative;
            min-height: 540px;
        }

        .devices-enable-grid {
            display: grid;
            gap: 14px;
            height: 100%;
            align-items: stretch;
            min-height: 0;
            padding: 10px;
        }

        .devices-enable-grid.with-toolbar {
            grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
        }

        .devices-enable-grid.without-toolbar {
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
        }

        .devices-enable-toolbar-column {
            display: flex;
            align-items: center;
            justify-content: center;
            min-width: 52px;
        }

        .devices-enable-toolbar-column .k-listbox-actions {
            display: flex;
            flex-direction: column;
            gap: 8px;
            align-items: stretch;
        }

        .devices-enable-toolbar-column .k-listbox-actions .k-button {
            margin: 0;
        }

        .devices-enable-toolbar-column .k-listbox-actions .k-button + .k-button {
            margin-left: 0;
            margin-top: 8px;
        }

        .devices-enable-panel {
            background: linear-gradient(180deg, #f8fcff 0%, #eef5fb 100%);
            border: 1px solid #d4e3f1;
            border-radius: 14px;
            box-shadow: 0 10px 22px rgba(16, 52, 83, 0.08);
            padding: 12px;
            display: flex;
            flex-direction: column;
            min-height: 0;
            overflow: hidden;
        }

        .devices-enable-title {
            color: #0f577d;
            font-size: 16px;
            margin: 2px 0 8px;
            letter-spacing: 0.01em;
        }

        .devices-enable-search {
            margin: 4px 0 8px;
        }

        .devices-enable-search .k-textbox {
            border: 1px solid #bad1e5 !important;
            background: #ffffff;
            box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.08);
            width: 100%;
        }

        .devices-enable-listbox {
            flex: 1;
            min-height: 0;
            border-radius: 10px;
            border: 1px solid #c8dced;
            background: #ffffff;
            overflow: hidden;
        }

        .devices-enable-listbox .k-list-scroller {
            background: #ffffff;
        }

        .devices-enable-listbox .k-list-item:hover {
            background: rgba(1, 140, 165, 0.08);
        }

        .devices-enable-listbox .k-selected {
            background: #d8eef6 !important;
            color: #0d4f71 !important;
        }

        @media (max-width: 1100px) {
            .devices-enable-root {
                height: auto;
                min-height: 0;
            }

            .devices-enable-grid {
                grid-template-columns: 1fr;
                height: auto;
            }

            .devices-enable-toolbar-column {
                min-width: 0;
                padding: 4px 0;
            }

            .devices-enable-listbox {
                min-height: 270px;
            }
        }
    `;
    

    return (
        <div className='container devices-enable-root'>
            <style>{devicesEnableCss}</style>
            {loading && loadingPanel}
            {error && <p className="text-danger" style={{ zIndex: 999, position: 'relative', width: '65%' }}>{error}</p>}            
            {(activeDevicesInfo.length > 0 || inactiveDevicesInfo.length > 0) &&
                <div 
                    className={`devices-enable-grid ${canTransferDevices ? 'with-toolbar' : 'without-toolbar'}`}
                >

                    <NotificationGroup
                        style={{
                            right: 12,
                            bottom: 50,
                            position: "fixed",
                            zIndex: 1200,
                            alignItems: "flex-start",
                            flexWrap: "wrap-reverse",
                        }}
                    >
                        <Fade enter={true} exit={true}>
                            {messageEnabledDevice && (
                                <Notification
                                    type={{ style: "success", icon: true }}
                                    //closable={true}
                                    onClose={() => setMessageEnabledDevice(false)}
                                >
                                    <span>{localizationService.toLanguageString('configuration.devices.device_enabled', 'NOT_FOUND')}</span>
                                </Notification>
                            )}
                            {messageDisabledDevice && (
                                <Notification
                                    type={{ style: "warning", icon: true }}
                                    //closable={true}
                                    onClose={() => setMessageDisabledDevice(false)}
                                >
                                    <span>{localizationService.toLanguageString('configuration.devices.device_disabled', 'NOT_FOUND')}</span>
                                </Notification>
                            )}
                            {messageEnabledDevices && (
                                <Notification
                                    type={{ style: "success", icon: true }}
                                    //closable={true}
                                    onClose={() => setMessageEnabledDevices(false)}
                                >
                                    <span>{localizationService.toLanguageString('configuration.devices.devices_enabled', 'NOT_FOUND')}</span>
                                </Notification>
                            )}
                            {messageDisabledDevices && (
                                <Notification
                                    type={{ style: "success", icon: true }}
                                    //closable={true}
                                    onClose={() => setMessageDisabledDevices(false)}
                                >
                                    <span>{localizationService.toLanguageString('configuration.devices.devices_disabled', 'NOT_FOUND')}</span>
                                </Notification>
                            )}
                        </Fade>
                    </NotificationGroup>

                    <div className='devices-enable-panel'>
                        <h6 className='devices-enable-title'>{localizationService.toLanguageString('configuration.devices.enabled', 'NOT_FOUND')} ({state.activeDevices?.length})</h6>

                        <div className="k-listview-item devices-enable-search" style={{ display: 'block' }}>
                            <fieldset style={{ display: 'flex' }} >
                                <TextBox
                                    style={{
                                        border: '2px solid #bad1e5',
                                        boxShadow: 'inset 0px 0px 5px 0px rgba(0,0,0,0.13)',
                                    }}
                                    onChange={onSearchActiveDevice}
                                    placeholder={localizationService.toLanguageString('configuration.devices.search_device', 'NOT_FOUND')}
                                    prefix={() => (
                                        <SvgIcon icon={searchIcon} svgStyle={{ margin: drawerExpanded ? '20px -5px 0px 5px' : '0px -5px 0px 5px' }}  />
                                    )}
                                />
                            </fieldset>
                        </div>

                        <ListBox
                            className='devices-enable-listbox'
                            style={{ height: '100%', width: '100%' }}
                            data={searchActiveDevice !== null ? searchActiveDevice : activeDevicesInfo}
                            textField='name'
                            selectedField={SELECTED_FIELD}
                            onItemClick={(e: ListBoxItemClickEvent) => handleItemClick(e, 'activeDevices', 'inactiveDevices')}
                            onDragStart={handleDragStart}
                            onDrop={handleDrop}
                            item={MyCustomItem}
                            draggable={userType === 'UserAdminOnlyLocator' ? false : true}
                        />
                    </div>

                    {canTransferDevices && (
                        <div className='devices-enable-toolbar-column'>
                            <ListBoxToolbar
                                tools={['transferTo', 'transferFrom']}
                                data={state.activeDevices}
                                dataConnected={state.inactiveDevices}
                                onToolClick={handleToolBarClick}
                            />
                        </div>
                    )}

                    <div className='devices-enable-panel'>
                        <h6 className='devices-enable-title'>{localizationService.toLanguageString('configuration.devices.disabled', 'NOT_FOUND')} ({state.inactiveDevices?.length})</h6>

                        <div className="k-listview-item devices-enable-search" style={{ display: 'block' }}>
                            <fieldset style={{ display: 'flex' }} >
                                <TextBox
                                    style={{
                                        border: '2px solid #bad1e5',
                                        boxShadow: 'inset 0px 0px 5px 0px rgba(0,0,0,0.13)',
                                    }}
                                    onChange={onSearchInactiveDevice}
                                    placeholder={localizationService.toLanguageString('configuration.devices.search_device', 'NOT_FOUND')}
                                    prefix={() => (
                                        <SvgIcon icon={searchIcon} svgStyle={{ margin: drawerExpanded ? '20px -5px 0px 5px' : '0px -5px 0px 5px' }}  />
                                    )}
                                />
                            </fieldset>
                        </div>

                        <ListBox
                            className='devices-enable-listbox'
                            style={{ height: '100%', width: '100%' }}
                            data={searchInactiveDevice !== null ? searchInactiveDevice : inactiveDevicesInfo}
                            textField='name'
                            selectedField={SELECTED_FIELD}
                            onItemClick={(e: ListBoxItemClickEvent) => handleItemClick(e, 'inactiveDevices', 'activeDevices')}
                            onDragStart={handleDragStart}
                            onDrop={handleDrop}
                            draggable={userType === 'UserAdminOnlyLocator' ? false : true}
                            item={MyCustomItem}
                        />
                    </div>
                </div>
            }
        </div>
    );

}
